/**
 * Runtime Configuration Loader
 * 
 * This module loads configuration from a JSON file at runtime instead of
 * relying on build-time environment variables. This allows the same build
 * to work in different environments (development, staging, production)
 * by simply changing the config.json file.
 * 
 * The config.json file should be placed in the web-client/dist directory
 * and served by NGINX at the root path (/config.json).
 */

export interface RuntimeConfig {
  /** Base URL for the backend API (e.g., 'https://pivot.deluj.app/api/v1') */
  apiUrl: string;
  
  /** Environment identifier (development, staging, production) */
  environment: string;
  
  /** 
   * Map tile server URL template.
   * Format: /tiles/{layerId}/{z}/{x}/{y}.png
   * 
   * IMPORTANT: 
   * - Use a relative URL (e.g., '/tiles/1/{z}/{x}/{y}.png') to ensure tiles go 
   *   through the NGINX proxy which adds CORS headers.
   * - The {layerId} placeholder should be replaced with an actual layer ID 
   *   configured in the tile server database.
   * - Direct URLs to external tile servers (like tile.openstreetmap.org) will
   *   cause CORS errors because those servers don't allow cross-origin requests.
   */
  mapTileUrl: string;
  
  /** Realm sync server URL (e.g., 'https://pivot.deluj.app/realm-sync') */
  realmSyncUrl?: string;
  
  /** Tile server URL prefix (e.g., '/tiles') - used to construct full tile URLs */
  tileServerUrl?: string;
}

/**
 * Default configuration values used during development if config.json is not available.
 * 
 * CRITICAL: The default mapTileUrl uses a relative path '/tiles/1/{z}/{x}/{y}.png'
 * to ensure all tile requests go through the NGINX proxy. This prevents CORS errors
 * that occur when trying to fetch tiles directly from external providers like
 * OpenStreetMap.
 * 
 * Note: Layer ID '1' is used as the default. You must configure a tile layer
 * with ID=1 in the tile_server.tile_layer table, or update this to match your
 * configured layer ID.
 */
const defaultConfig: RuntimeConfig = {
  apiUrl: 'http://localhost:8081/api/v1',
  environment: 'development',
  // Use relative URL with default layer ID (1) to go through NGINX proxy
  // Format: /tiles/{layerId}/{z}/{x}/{y}.png
  mapTileUrl: '/tiles/1/{z}/{x}/{y}.png',
  realmSyncUrl: 'http://localhost:8081/realm-sync',
  tileServerUrl: '/tiles',
};

/**
 * Cached configuration to avoid multiple fetches.
 */
let cachedConfig: RuntimeConfig | null = null;

/**
 * Fetches the runtime configuration from the server.
 * 
 * @returns Promise resolving to the RuntimeConfig object
 * @throws Error if config.json cannot be fetched or parsed
 */
export async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  // Return cached config if already loaded
  if (cachedConfig) {
    return cachedConfig;
  }

  try {
    // Fetch config.json from the server
    const response = await fetch('/config.json', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      // Config file not found, use defaults
      console.warn('[RuntimeConfig] config.json not found, using defaults');
      cachedConfig = defaultConfig;
      return cachedConfig;
    }

    const config = await response.json() as RuntimeConfig;
    
    // Validate required fields
    if (!config.apiUrl) {
      console.error('[RuntimeConfig] Missing required field: apiUrl');
      cachedConfig = defaultConfig;
      return cachedConfig;
    }

    // Validate mapTileUrl - warn if it's an external URL that might cause CORS issues
    if (config.mapTileUrl && !config.mapTileUrl.startsWith('/')) {
      if (!config.mapTileUrl.startsWith('http://localhost') && 
          !config.mapTileUrl.startsWith('https://localhost')) {
        console.warn('[RuntimeConfig] External tile URL detected. Ensure the server has proper CORS headers:', config.mapTileUrl);
      }
    }

    // Merge with defaults for optional fields
    cachedConfig = {
      ...defaultConfig,
      ...config,
    };

    console.log('[RuntimeConfig] Loaded configuration:', {
      apiUrl: cachedConfig.apiUrl,
      environment: cachedConfig.environment,
      mapTileUrl: cachedConfig.mapTileUrl,
    });

    return cachedConfig;
  } catch (error) {
    console.error('[RuntimeConfig] Failed to load config.json, using defaults:', error);
    cachedConfig = defaultConfig;
    return cachedConfig;
  }
}

/**
 * Gets the current runtime configuration.
 * 
 * @returns The RuntimeConfig object (uses defaults if not yet loaded)
 */
export function getConfig(): RuntimeConfig {
  return cachedConfig || defaultConfig;
}

/**
 * Clears the cached configuration (useful for testing).
 */
export function clearConfigCache(): void {
  cachedConfig = null;
}